import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useCart, money } from "../lib/cart";
import { Button } from "../components/ui/button";
import { FileCheck, ArrowLeft } from "lucide-react";
import { ProductImage } from "../components/ProductImage";
import { ProductOptions } from "../components/StrengthPicker";
import { optionKind, isSkincare, detailPath } from "../lib/product";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [variantId, setVariantId] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      const vs = r.data.variants || [];
      setVariantId(vs[0]?.id || null);
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#5E4D5A]">Product not found. <Link to={pathname.startsWith("/skincare") ? "/skincare" : "/shop"} className="underline">Back to shop</Link></div>;
  if (!product) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#5E4D5A]">Loading…</div>;

  const variant = (product.variants || []).find((v) => v.id === variantId);
  const skincare = isSkincare(product);
  if (skincare !== pathname.startsWith("/skincare")) return <Navigate to={detailPath(product)} replace />;
  const back = skincare ? { to: "/skincare", label: "Back to skincare" } : { to: "/shop", label: "Back to catalog" };

  const addToCart = () => {
    if (!variant) return;
    add(product, variant, qty);
    toast.success(`Added ${qty} × ${product.name} ${variant.strength} to cart`);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
      <Link to={back.to} className="inline-flex items-center gap-2 text-sm text-[#5E4D5A] hover:text-[#59394F] mb-10 transition-colors" data-testid="back-to-shop">
        <ArrowLeft size={14} /> {back.label}
      </Link>

      <div className="grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <ProductImage imageId={product.images?.[active]?.id} variant="large" alt={product.name} iconSize={120} className="rounded-3xl border border-[#A8B59A]/20" testId="product-main-image" />
          {product.images?.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-thumbs">
              {product.images.map((img, i) => (
                <button key={img.id} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`} className={`rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-[#C99898]" : "border-transparent hover:border-[#A8B59A]/40"}`}>
                  <ProductImage imageId={img.id} variant="thumb" alt={`${product.name} photo ${i + 1}`} iconSize={20} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51] mb-3" data-testid="product-category">{skincare ? "Skin Booster Serums" : "Research catalog"}</p>
          <h1 className="font-serif text-5xl text-[#5A433B] leading-tight">{product.name}</h1>
          {skincare && product.description && (
            <p className="mt-5 text-[15px] text-[#5E4D5A] leading-relaxed whitespace-pre-line max-w-md" data-testid="product-description">{product.description}</p>
          )}

          {product.form && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5E6B51]">Form</div>
              <div className="mt-1 text-sm text-[#5A433B]">{product.form}</div>
            </div>
          )}

          <div className="mt-6">
            <ProductOptions variants={product.variants || []} value={variantId} onChange={(v) => { setVariantId(v); setQty(1); }} kind={optionKind(product)} />
          </div>

          <div className="mt-8 py-6 border-y border-[#A8B59A]/20 flex flex-wrap items-end gap-6">
            <div className="font-serif text-4xl" data-testid="variant-price">{variant ? money(variant.price_cents) : ""}</div>
            <div className="flex items-center bg-white rounded-full border border-[#A8B59A]/30 overflow-hidden">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2 text-[#5A433B] hover:bg-[#A8B59A]/10 transition-colors">−</button>
              <span data-testid="qty-value" className="px-4 text-sm w-10 text-center">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(100, q + 1))} className="px-4 py-2 text-[#5A433B] hover:bg-[#A8B59A]/10 transition-colors">+</button>
            </div>
          </div>

          <Button onClick={addToCart} disabled={!variant} data-testid="add-to-cart-btn" className="mt-6 rounded-full bg-[#59394F] hover:bg-[#432A3B] text-[#F4EBDD] px-10 py-6 h-auto text-base transition-colors">
            Add to cart
          </Button>

          {!skincare && (
            <>
          <div className="mt-10 p-6 bg-[#A8B59A]/10 rounded-2xl border border-[#A8B59A]/20">
            <div className="flex items-center gap-3 mb-2">
              <FileCheck size={18} className="text-[#5E6B51]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#5E6B51]">Documentation</span>
            </div>
            <p className="text-sm text-[#5A433B] leading-relaxed">
              Certificates of Analysis are available for each batch when applicable. <Link to="/coas" className="underline hover:text-[#59394F] transition-colors">Browse the COA library</Link>.
            </p>
          </div>

          <div className="mt-6 p-6 bg-[#C99898]/10 rounded-2xl border border-[#C99898]/20">
            <p className="text-xs uppercase tracking-[0.3em] text-[#59394F] mb-2">Research use only</p>
            <p className="text-sm text-[#5A433B] leading-relaxed">Not for human or animal consumption. No claims about safety, efficacy, treatment, or dosing. By ordering you confirm that you are a qualified researcher.</p>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
